import { Box, Grid, Toolbar, IconButton } from "@mui/material";
import update from "immutability-helper";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getFilters, removeFilter, saveFilter } from "../../../services/filter.service";
import {
	setLockTotal as setLockTotalComparison,
	setSelectedFilter as setSelectedFilterComparison,
} from "../../../store/reducers/comparisonReducers/hospitalComparison.slice";
import { setFilterDateRange, setLockTotal, setSelectedFilter } from "../../../store/reducers/hospital.slice";
import { setFacilityIds, setMultipleFacility } from "../../../store/reducers/permission.slice";
import { ADD_NOTIFICATION } from "../../../store/types";
import { DASHBOARD_FILTER_TYPE } from "../../../types/common.type";
import { PAGE_TYPE } from "../../../types/pages.type";
import { calcProPercentsBasedOnFilterAndDays, getPercentageByTotal } from "../../../utilis/common";
import DateFilterComponent from "../../../components/dashboard/filter/DateFilterComponent";
import DashboardHeader from "../../../components/dashboard/header/DashboardHeader";
import CensusAverageBox from "../../../components/dashboard/shared/CensusAverageBox/CensusAverageBox";
import DashboardTotalCount from "../../../components/dashboard/shared/DashboardTotalCount";
import HeaderFilterOptions from "../../../components/dashboard/shared/headerFilterOptions/HeaderFilterOptions";
import HeaderMenu from "../../../components/shared/header-menu";
import HospitalHeaderFilterList, { headerItemsData } from "./HospitalHeaderFilterList";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import useFacilityId from "../../../hooks/useFacilityId";


const HospitalHeader = ({
	handleFilterUpdate,
	loading,
	setLoading,
	setLoadingComparison,
	filter,
	projectionDays,
	isComparingAgainstAvgCensus,
	percentageAgainst,
	filterListData,
	dynamicCards,
	isLayoutMode = false,
	disableInteractions = false,
	isHeaderVisible,
	setIsHeaderVisible,
	isDataFromCache = false,
	pageConverterPatientList = [],
	pageConverterFilter = null,
	renderCardFilterPopover,
	interactiveHeaderFilterPopoverSlowTransition = false,
}) => {
	const dispatch = useDispatch();
	const [selectedFilterMenu, setSelectedFilterMenu] = useState("all");
	const [filterValue, setFilterValue] = useState("");
	const [filterOptions, setFilterOptions] = useState([]);
	const isMobile = useIsMobileDevice();

	const { dbData, cardFilter, transferType, filterTotal, lockedTotal, lockedTotalBy } = useSelector(
		(state) => state.hospital
	);

	const { percentageBy } = useSelector((state) => state.common);
	const { lockedTotal: lockedTotalComparison, filterTotal: filterTotalComparison } = useSelector(
		(state) => state.hospitalComparison
	);
	const { facilityIds, isMultipleFacility } = useSelector((state) => state.permission);
	const { facilityId } = useFacilityId();
	const { isAutomaticReportSaved = false } = dbData;
	const totalFilterForViewer = calcProPercentsBasedOnFilterAndDays(
		filterTotal,
		filter,
		projectionDays,
		true,
		!isComparingAgainstAvgCensus || (lockedTotalBy && lockedTotalBy !== "census")
	);

	const censusAverage = useMemo(() => {
		return getPercentageByTotal(dbData);
	}, [dbData]);

	const { facilities: activeFacilities, prevStateFacilities } = useSelector((state) => state.activeFacilities);

	const toggleHeader = useCallback(() => {
		setIsHeaderVisible(prev => {
			const newState = !prev;
			return newState;
		});
	}, [setIsHeaderVisible]);

	const handleChangeSelect = useCallback(
		(event) => {
			const selectedValue = event.target.value;

			if (selectedValue && selectedValue.facilityIds && selectedValue.facilityIds.length > 1) {
				dispatch(setFacilityIds(selectedValue.facilityIds));
				dispatch(setMultipleFacility(true));
			} else {
				dispatch(setFacilityIds([]));
				dispatch(setMultipleFacility(false));
			}

			handleFilterUpdate(true);
			let latestValues = {
				...selectedValue?.filter,
				filter: {
					...selectedValue?.filter.filter,
					startDate: moment(selectedValue?.filter.filter.startDate).toDate(),
					endDate: moment(selectedValue?.filter.filter.endDate).toDate(),
				},
			};
			setSelectedFilterMenu({ ...selectedValue, filter: latestValues });
			dispatch(setSelectedFilter({ ...selectedValue, filter: latestValues }));
		},
		[dispatch, handleFilterUpdate]
	);

	const getFiltersData = useCallback(() => {
		getFilters({ type: DASHBOARD_FILTER_TYPE.HOSPITAL }).then((res) => {
			if (res && res.length > 0) {
				setFilterOptions(res);
			}
		});
	}, []);

	useEffect(() => {
		if (facilityId) {
			getFiltersData();
		}
	}, [getFiltersData, facilityId]);

	const handleClearFilter = useCallback(() => {
		setSelectedFilterMenu("all");
		dispatch(setSelectedFilter("all"));
		dispatch(setSelectedFilterComparison("all"));
	}, [dispatch]);

	useEffect(() => {
		// Skip reset if prevStateFacilities is empty (initial state) - this is first load, not a facility change
		const isInitialLoad = prevStateFacilities.length === 0 && activeFacilities.length > 0;
		
		// Check if cache exists for current filter to prevent unnecessary reset
		const hasCache = isDataFromCache;
		
		if (!_.isEqual(activeFacilities, prevStateFacilities)) {
			// Skip reset on initial load (prevStateFacilities is empty) or when cache exists
			if (!isInitialLoad && !hasCache) {
				handleClearFilter();
			}
		}
	}, [activeFacilities, handleClearFilter, prevStateFacilities, isDataFromCache]);

	const handleRemoveFilter = async (selectedItem) => {
		await removeFilter(selectedItem._id);
		const latestFacilityList = update(filterOptions, {
			$splice: [[filterOptions.indexOf(selectedItem), 1]],
		});
		setFilterOptions(latestFacilityList);
		return true;
	};

	const handleSaveFilter = async () => {
		let facilityIdsData = [];
		if (isMultipleFacility) {
			facilityIdsData = facilityIds;
		} else {
			facilityIdsData = facilityId ? [facilityId] : [];
		}

		const resData = await saveFilter({
			type: DASHBOARD_FILTER_TYPE.HOSPITAL,
			name: filterValue,
			facilityIds: facilityIdsData,
			filter: {
				cardFilter,
				filter,
				transferType,
			},
		});
		if (resData && resData.status === 200) {
			getFiltersData();
			setFilterValue("");
			return true;
		} else {
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					label: resData?.error,
					type: "error",
					id: "hospitalSaveFilterError",
				},
			});
			return false;
		}
	};

	const onChangeFilter = useCallback(
		(value) => {
			dispatch(setFilterDateRange(value));
		},
		[dispatch]
	);

	const handleSetDefaultTotal = useCallback(() => {
		dispatch(setLockTotal(lockedTotal ? null : filterTotal));
		dispatch(setLockTotalComparison(lockedTotalComparison ? null : filterTotalComparison));
	}, [dispatch, filterTotal, filterTotalComparison, lockedTotal, lockedTotalComparison]);

	return (
		<>
			{/* Mobile Filter Toggle Button - Only visible on mobile */}
			{isMobile && (
				<Box sx={{
					position: "fixed",
					top: "66px", // Position below the main header
					right: "10px",
					zIndex: 900, // Higher than Header (1200) and DashboardHeader (500)
				}}>
					<IconButton
						onClick={toggleHeader}
						sx={{
							backgroundColor: "rgba(99, 101, 120, 0.9)",
							color: "white",
							width: "25px",
							height: "25px",
							"&:hover": {
								backgroundColor: "rgba(99, 101, 120, 1)"
							},
							boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
							border: "2px solid rgba(255,255,255,0.2)"
						}}
					>
						{isHeaderVisible ? <KeyboardArrowUpIcon /> : <FilterListIcon />}
					</IconButton>
				</Box>
			)}

			<DashboardHeader isHeaderVisible={isHeaderVisible}>
				<Box
					display={"flex"}
					sx={{
						width: "100%",
						height: isMobile ? "40px" : "60px",
						backgroundColor: "#636578",
						display: "flex",
						alignItems: "center",
						padding: "0px 16px",
						cursor: disableInteractions ? "default" : "pointer",
						pointerEvents: disableInteractions ? "none" : "auto",
						opacity: disableInteractions ? 0.5 : 1,
						"@media (max-width: 768px)": {
							height: "45px",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px"
						}
					}}
				>
					<DashboardTotalCount
						isLockedTotal={lockedTotal ? true : false}
						filterTotal={totalFilterForViewer}
						handleChange={disableInteractions ? undefined : handleSetDefaultTotal}
						percentageBy={percentageBy}
						cardFilter={cardFilter}
						disabled={disableInteractions}
						// onMobileClick={handleMobileFilterMenuOpen}
						isMobile={isMobile}
						filterListData={filterListData}
					/>
					{!isMobile && (
						<Grid
							container
							direction={"row"}
							justifyContent="space-between"
							alignItems="center"
							sx={{ ml: "-10px", mt: "10px" }}
						>
							<Grid item lg={8} md={6} xs={12}>
								<HospitalHeaderFilterList
									items={headerItemsData}
									filterListData={filterListData}
									cardFilter={cardFilter}
									transferType={transferType}
									percentageBy={percentageBy}
									lockedTotal={lockedTotal}
									lockedTotalBy={lockedTotalBy}
									dynamicCards={dynamicCards}
									disableInteractions={disableInteractions}
									renderCardFilterPopover={renderCardFilterPopover}
									interactiveHeaderFilterPopoverSlowTransition={interactiveHeaderFilterPopoverSlowTransition}
								/>
							</Grid>
							<Grid item lg={4} md={6} xs={12}>
								<HeaderFilterOptions
									selectedFilterMenu={selectedFilterMenu}
									handleChangeSelect={disableInteractions ? undefined : handleChangeSelect}
									filterOptions={filterOptions}
									filterValue={filterValue}
									setFilterValue={disableInteractions ? undefined : setFilterValue}
									handleRemoveFilter={disableInteractions ? undefined : handleRemoveFilter}
									handleSaveFilter={disableInteractions ? undefined : handleSaveFilter}
									handleClearFilter={disableInteractions ? undefined : handleClearFilter}
									disabled={disableInteractions}
									isMobile={isMobile}
								/>
							</Grid>
						</Grid>
					)}
					{isMobile && <HospitalHeaderFilterList
						items={headerItemsData}
						filterListData={filterListData}
						cardFilter={cardFilter}
						transferType={transferType}
						percentageBy={percentageBy}
						lockedTotal={lockedTotal}
						lockedTotalBy={lockedTotalBy}
						dynamicCards={dynamicCards}
						disableInteractions={disableInteractions}
						isMobile={isMobile}
						renderCardFilterPopover={renderCardFilterPopover}
						interactiveHeaderFilterPopoverSlowTransition={interactiveHeaderFilterPopoverSlowTransition}
					/>}
				</Box>
				<Toolbar sx={{
					height: 91,
					"@media (max-width: 768px)": {
						height: "auto",
						minHeight: "auto",
						padding: "1px 4px",
						flexDirection: "column",
						alignItems: "stretch",
						gap: "12px"
					}
				}}>
					<Grid
						container
						direction={"row"}
						spacing={isMobile ? 0 : 2}
						justifyContent="space-between"
						alignItems="center"
						sx={{
							ml: "10px",
							"@media (max-width: 768px)": {
								ml: "0px",
								flexDirection: "column",
								alignItems: "stretch",
								width: "100%",
								"& .MuiGrid-item": {
									width: "100%"
								}
							}
						}}
					>
						{!isMobile ? (
							<>
								<Grid item xs={4} md={4} xl={5}>
									<CensusAverageBox
										censusAverage={censusAverage}
										loading={loading}
										dbData={dbData}
										pageType={PAGE_TYPE.HOSPITAL}
										percentageAgainst={percentageAgainst}
										isLayoutMode={isLayoutMode}
										disableInteractions={disableInteractions}
									/>
								</Grid>
								<Grid item xs={8} md={8} xl={7} display="flex" justifyContent="space-between" alignItems="center">
									<DateFilterComponent
										handleFilterUpdate={disableInteractions ? undefined : handleFilterUpdate}
										onChangeFilter={disableInteractions ? undefined : onChangeFilter}
										filter={filter}
										loading={loading}
										setLoading={setLoading}
										page={PAGE_TYPE.HOSPITAL}
										isLayoutMode={isLayoutMode}
										disableInteractions={disableInteractions}
									/>
									<HeaderMenu
										page={PAGE_TYPE.HOSPITAL}
										loading={loading}
										setLoadingComparison={setLoadingComparison}
										isAutomaticReportSaved={isAutomaticReportSaved}
										activeFacilities={activeFacilities}
										dynamicCards={dynamicCards}
										isLayoutMode={isLayoutMode}
										disableInteractions={disableInteractions}
										pageConverterPatientList={pageConverterPatientList}
										pageConverterFilter={pageConverterFilter}
									/>
								</Grid>
							</>
						) : (
							<>
								<Grid item md={8} sm={12} display="flex" justifyContent="space-between" alignItems="center" sx={{
									"@media (max-width: 768px)": {
										flexDirection: "row",
										justifyContent: "space-between",
										alignItems: "center",
										gap: "8px"
									}
								}}>
									<Box sx={{
										width: "100%",
										"@media (max-width: 768px)": {
											width: "90%",
											display: "flex",
											justifyContent: "flex-start",
										}
									}}>
										<DateFilterComponent
											handleFilterUpdate={disableInteractions ? undefined : handleFilterUpdate}
											onChangeFilter={disableInteractions ? undefined : onChangeFilter}
											filter={filter}
											loading={loading}
											setLoading={setLoading}
											page={PAGE_TYPE.HOSPITAL}
											isLayoutMode={isLayoutMode}
											disableInteractions={disableInteractions}
											isMobile={isMobile}
										/>
									</Box>

									<Box sx={{
										"@media (max-width: 768px)": {
											width: "10%",
											display: "flex",
											justifyContent: "flex-end"
										}
									}}>
										<HeaderMenu
											page={PAGE_TYPE.HOSPITAL}
											loading={loading}
											setLoadingComparison={setLoadingComparison}
											isAutomaticReportSaved={isAutomaticReportSaved}
											activeFacilities={activeFacilities}
											dynamicCards={dynamicCards}
											isLayoutMode={isLayoutMode}
											disableInteractions={disableInteractions}
											isMobile={isMobile}
											pageConverterPatientList={pageConverterPatientList}
											pageConverterFilter={pageConverterFilter}
										/>
									</Box>
								</Grid>
								<Grid item md={4} sm={12}>
									<CensusAverageBox
										censusAverage={censusAverage}
										loading={loading}
										dbData={dbData}
										pageType={PAGE_TYPE.HOSPITAL}
										percentageAgainst={percentageAgainst}
										isLayoutMode={isLayoutMode}
										disableInteractions={disableInteractions}
										isMobile={isMobile}
									/>
								</Grid>
							</>
						)}
					</Grid>
				</Toolbar>
			</DashboardHeader>
		</>
	);
};

export default HospitalHeader;
